/**
 * BubbleMessage — UI-side discriminated union for the chat message renderer.
 *
 * Backend `ChatMessage` records map to `kind: 'plain'`. The other kinds
 * (thinking / contradiction / recap / system) are produced either by the
 * frontend (system notes) or by Phase 3.3 backend events.
 */

import type { ChatMessage } from '@shared/types.ts';
import { ENABLE_ADVANCED_BUBBLES } from '@/lib/featureFlags';

export type ThoughtChip = { text: string; icon?: string };

export type ContradictionOption = { id: string; label: string; detail?: string };

export type RecapOption = { id: string; label: string; kind?: 'primary' | 'ghost' };

export type RecapBullet = { text: string; refs?: string[] };

export type RecapSection = { cluster: string; bullets: RecapBullet[] };

export type BubbleMessage =
  | {
      kind: 'plain';
      id: string;
      role: 'INTERVIEWER' | 'INTERVIEWEE';
      content: string;
      createdAt?: string;
      refs?: string[];
    }
  | {
      kind: 'thinking';
      id: string;
      thoughts: ThoughtChip[];
    }
  | {
      kind: 'contradiction';
      id: string;
      createdAt?: string;
      earlier: { quote: string; refId?: string };
      now: { quote: string; refId?: string };
      question: string;
      options: ContradictionOption[];
    }
  | {
      kind: 'recap';
      id: string;
      createdAt?: string;
      title: string;
      intro: string;
      sections: RecapSection[];
      options: RecapOption[];
    }
  | {
      kind: 'system';
      id: string;
      text: string;
      createdAt?: string;
    };

/**
 * Adapt a backend ChatMessage to a BubbleMessage for rendering.
 *
 * Special case: when the content is a `[Canvas: "node"] body` injection
 * (added when the user attaches context to a canvas node), surface it as
 * a system note so it reads as a side-channel event, not as the user
 * "typing" the bracket-prefixed text.
 */
const CANVAS_INJECTION_RE = /^\[Canvas:\s*"([^"]+)"\]\s*([\s\S]*)$/;

export function chatMessageToBubble(msg: ChatMessage): BubbleMessage {
  const canvasMatch = msg.content.match(CANVAS_INJECTION_RE);
  if (canvasMatch) {
    const [, nodeLabel, text] = canvasMatch;
    const trimmed = text.trim();
    return {
      kind: 'system',
      id: msg.id,
      createdAt: msg.createdAt,
      text: trimmed
        ? `Context added on “${nodeLabel}”: ${trimmed}`
        : `Context added on “${nodeLabel}”.`,
    };
  }

  // Track C.1 — when the backend produces a structured `bubble` payload,
  // surface it as the matching BubbleMessage instead of plain text. The
  // payload lives at `msg.metadata.signals.bubble` (sanitiseSignals on the
  // backend assembles a single field, recap > contradiction precedence).
  if (ENABLE_ADVANCED_BUBBLES && msg.role === 'INTERVIEWER') {
    const meta = (msg.metadata ?? null) as Record<string, unknown> | null;
    const signals = meta?.signals as Record<string, unknown> | undefined;
    const raw = signals?.bubble;
    if (raw && typeof raw === 'object') {
      const b = raw as Record<string, unknown>;
      if (b.kind === 'contradiction') {
        const parsed = parseContradictionBubble(b);
        if (parsed) {
          return {
            kind: 'contradiction',
            id: msg.id,
            createdAt: msg.createdAt,
            ...parsed,
          };
        }
      } else if (b.kind === 'recap') {
        const parsed = parseRecapBubble(b);
        if (parsed) {
          return {
            kind: 'recap',
            id: msg.id,
            createdAt: msg.createdAt,
            ...parsed,
          };
        }
      }
    }
  }

  return {
    kind: 'plain',
    id: msg.id,
    role: msg.role,
    content: msg.content,
    createdAt: msg.createdAt,
  };
}

function parseContradictionBubble(raw: Record<string, unknown>):
  | {
      earlier: { quote: string; refId?: string };
      now: { quote: string; refId?: string };
      question: string;
      options: ContradictionOption[];
    }
  | null {
  const earlier = raw.earlier as Record<string, unknown> | undefined;
  const now = raw.now as Record<string, unknown> | undefined;
  const question = typeof raw.question === 'string' ? raw.question.trim() : '';
  const optionsRaw = Array.isArray(raw.options) ? raw.options : [];

  if (!earlier || typeof earlier.quote !== 'string' || !earlier.quote.trim()) return null;
  if (!now || typeof now.quote !== 'string' || !now.quote.trim()) return null;
  if (!question) return null;

  const options: ContradictionOption[] = optionsRaw
    .filter((o): o is Record<string, unknown> => !!o && typeof o === 'object')
    .map((o, i) => ({
      id: typeof o.id === 'string' && o.id ? o.id : `opt_${i}`,
      label: typeof o.label === 'string' ? o.label.trim() : '',
      detail: typeof o.detail === 'string' && o.detail.trim() ? o.detail.trim() : undefined,
    }))
    .filter((o) => o.label.length > 0);

  if (options.length === 0) return null;

  return {
    earlier: {
      quote: earlier.quote.trim(),
      ...(typeof earlier.refId === 'string' ? { refId: earlier.refId } : {}),
    },
    now: {
      quote: now.quote.trim(),
      ...(typeof now.refId === 'string' ? { refId: now.refId } : {}),
    },
    question,
    options,
  };
}

function parseRecapBubble(raw: Record<string, unknown>):
  | {
      title: string;
      intro: string;
      sections: RecapSection[];
      options: RecapOption[];
    }
  | null {
  const title = typeof raw.title === 'string' ? raw.title.trim() : '';
  const intro = typeof raw.intro === 'string' ? raw.intro.trim() : '';
  if (!title || !intro) return null;

  const sectionsRaw = Array.isArray(raw.sections) ? raw.sections : [];
  const sections: RecapSection[] = sectionsRaw
    .filter((s): s is Record<string, unknown> => !!s && typeof s === 'object')
    .map((s) => {
      const cluster = typeof s.cluster === 'string' ? s.cluster.trim() : '';
      const bulletsRaw = Array.isArray(s.bullets) ? s.bullets : [];
      const bullets: RecapBullet[] = bulletsRaw
        .filter((b): b is Record<string, unknown> => !!b && typeof b === 'object')
        .map((b) => {
          const text = typeof b.text === 'string' ? b.text.trim() : '';
          const refs = Array.isArray(b.refs)
            ? b.refs.filter((r): r is string => typeof r === 'string')
            : undefined;
          return { text, ...(refs && refs.length > 0 ? { refs } : {}) };
        })
        .filter((b) => b.text.length > 0);
      return { cluster, bullets };
    })
    .filter((s) => s.cluster.length > 0 && s.bullets.length > 0);

  if (sections.length === 0) return null;

  const optionsRaw = Array.isArray(raw.options) ? raw.options : [];
  const options: RecapOption[] = optionsRaw
    .filter((o): o is Record<string, unknown> => !!o && typeof o === 'object')
    .map((o, i) => {
      const id = typeof o.id === 'string' && o.id ? o.id : `opt_${i}`;
      const label = typeof o.label === 'string' ? o.label.trim() : '';
      const kind: 'primary' | 'ghost' | undefined =
        o.kind === 'primary' || o.kind === 'ghost' ? o.kind : undefined;
      return { id, label, ...(kind ? { kind } : {}) };
    })
    .filter((o) => o.label.length > 0);

  if (options.length === 0) return null;

  return { title, intro, sections, options };
}

/** Format an ISO timestamp into HH:mm — used in bubble meta lines. */
export function formatBubbleTime(timestamp?: string): string {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}
